/**
 * KI-Küchenleiter - Intelligente Küchenpriorisierung
 * 
 * Features:
 * - Automatische Priorisierung (NICHT FIFO!)
 * - 3 Spalten: "Jetzt zubereiten", "Als Nächstes", "Kann warten"
 * - Aktive Warnungen bei drohenden Verspätungen
 * - Touch-optimiert für Küchenumgebung
 * 
 * Erfolgskriterium: "Die Küche weiß immer, was jetzt dran ist – ohne Stress."
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChefHat, Clock, AlertTriangle, CheckCircle2, 
  Flame, Timer, Truck, Users, Coffee, 
  Volume2, VolumeX, Maximize, X, Bell,
  ArrowRight, QrCode, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sound für neue Bestellungen
const playAlertSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleBA0teleBAA=');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {}
};

// Prioritäts-Farben
const PRIORITY_COLORS = {
  urgent: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', badge: 'bg-red-500' },
  high: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', badge: 'bg-orange-500' },
  normal: { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', badge: 'bg-blue-500' },
  low: { bg: 'bg-slate-500/20', border: 'border-slate-600', text: 'text-slate-400', badge: 'bg-slate-500' }
};

// Order Type Icons
const ORDER_TYPE_ICONS = {
  delivery: Truck,
  pickup: Coffee,
  dine_in: Users,
  qr_code: QrCode
};

// Order Type Labels
const ORDER_TYPE_LABELS = {
  delivery: 'Lieferung',
  pickup: 'Abholung',
  dine_in: 'Vor-Ort',
  qr_code: 'QR'
};

// Alert Component
function Alert({ alert, onDismiss }) {
  const bgColor = alert.type === 'critical' ? 'bg-red-500/90' : 
                  alert.type === 'warning' ? 'bg-orange-500/90' : 
                  alert.type === 'overload' ? 'bg-purple-500/90' : 'bg-blue-500/90';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      className={`${bgColor} rounded-xl p-4 shadow-lg flex items-center gap-3`}
    >
      <div className="p-2 bg-white/20 rounded-lg">
        {alert.type === 'overload' ? (
          <Flame className="w-6 h-6 text-white" />
        ) : (
          <AlertTriangle className="w-6 h-6 text-white" />
        )}
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-white">{alert.title}</h4>
        <p className="text-white/90 text-sm">{alert.message}</p>
      </div>
      <button 
        onClick={() => onDismiss(alert.alert_id)}
        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
      >
        <X className="w-5 h-5 text-white" />
      </button>
    </motion.div>
  );
}

// Order Card Component
function OrderCard({ order, onBump }) {
  const colors = PRIORITY_COLORS[order.priority_level] || PRIORITY_COLORS.normal;
  const TypeIcon = ORDER_TYPE_ICONS[order.order_type] || Users;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`${colors.bg} border-2 ${colors.border} rounded-2xl p-4 cursor-pointer 
        hover:scale-[1.02] transition-all duration-200 active:scale-[0.98]`}
      onClick={() => onBump(order.order_id)}
      data-testid={`order-card-${order.order_id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-white">{order.table_name}</span>
          <div className={`p-1.5 ${colors.badge} rounded-lg`}>
            <TypeIcon className="w-4 h-4 text-white" />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Priority Badge */}
          {order.priority_level === 'urgent' && (
            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
              DRINGEND
            </span>
          )}
          
          {/* Wait Time */}
          <div className={`flex items-center gap-1 ${colors.text}`}>
            <Timer className="w-4 h-4" />
            <span className="text-sm font-medium">{Math.round(order.wait_minutes)} Min</span>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {order.warnings && order.warnings.length > 0 && (
        <div className="mb-3 space-y-1">
          {order.warnings.map((warning, idx) => (
            <div key={idx} className={`text-xs ${warning.type === 'critical' ? 'text-red-400' : 'text-orange-400'} flex items-center gap-1`}>
              <AlertTriangle className="w-3 h-3" />
              {warning.message}
            </div>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="space-y-2">
        {order.items?.slice(0, 6).map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                {item.quantity}
              </span>
              <span className="font-medium truncate max-w-[200px]">{item.product_name || item.name}</span>
            </div>
          </div>
        ))}
        {order.items?.length > 6 && (
          <div className="text-slate-400 text-sm">
            +{order.items.length - 6} weitere Artikel
          </div>
        )}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="mt-3 p-2 bg-black/20 rounded-lg">
          <p className="text-sm text-yellow-300">📝 {order.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-slate-400">
          {ORDER_TYPE_LABELS[order.order_type] || 'Vor-Ort'}
          {order.time_remaining !== null && order.time_remaining !== undefined && (
            <span className="ml-2">• Ziel in {order.time_remaining} Min</span>
          )}
        </div>
        
        <div className="flex items-center gap-1 text-emerald-400">
          <span className="text-xs">Tippen = Fertig</span>
          <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </motion.div>
  );
}

// Column Component
function KitchenColumn({ title, icon: Icon, orders, color, bgColor, onBump }) {
  return (
    <div className={`flex-1 ${bgColor} rounded-2xl p-4 flex flex-col min-h-0`}>
      {/* Column Header */}
      <div className={`flex items-center gap-3 mb-4 pb-3 border-b border-white/10`}>
        <div className={`p-2 ${color} rounded-xl`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <p className="text-sm text-slate-400">{orders.length} Bestellungen</p>
        </div>
      </div>

      {/* Orders */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        <AnimatePresence mode="popLayout">
          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-full flex items-center justify-center"
            >
              <div className="text-center text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Alles erledigt!</p>
              </div>
            </motion.div>
          ) : (
            orders.map((order) => (
              <OrderCard key={order.order_id} order={order} onBump={onBump} />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Main Component
export default function AIKitchenDisplay() {
  const [data, setData] = useState({ now: [], next: [], later: [], alerts: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const previousOrderCount = useRef(0);
  
  const sessionToken = localStorage.getItem('access_token') || localStorage.getItem('access_token');

  // Daten laden
  const loadData = useCallback(async () => {
    try {
      // Demo-Endpoint verwenden wenn nicht eingeloggt
      const url = sessionToken 
        ? `${BACKEND_URL}/api/ai-kitchen/orders`
        : `${BACKEND_URL}/api/ai-kitchen/demo`;
      
      const headers = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
      
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        // Fallback zu Demo
        const demoResponse = await fetch(`${BACKEND_URL}/api/ai-kitchen/demo`);
        if (demoResponse.ok) {
          const demoData = await demoResponse.json();
          setData(demoData);
        }
        return;
      }
      
      const newData = await response.json();
      
      // Sound bei neuen Bestellungen
      const newCount = newData.now.length + newData.next.length + newData.later.length;
      if (soundEnabled && newCount > previousOrderCount.current && previousOrderCount.current > 0) {
        playAlertSound();
      }
      previousOrderCount.current = newCount;
      
      setData(newData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, soundEnabled]);

  // Auto-Refresh alle 5 Sekunden
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Bestellung bumpen (Status ändern)
  const handleBump = async (orderId) => {
    try {
      const url = sessionToken 
        ? `${BACKEND_URL}/api/ai-kitchen/orders/${orderId}/bump`
        : `${BACKEND_URL}/api/kds/status/${orderId}`;
      
      const headers = sessionToken 
        ? { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' };
      
      await fetch(url, { 
        method: 'POST', 
        headers,
        body: JSON.stringify({ status: 'ready' })
      });
      
      // Daten neu laden
      loadData();
    } catch (error) {
      console.error('Fehler beim Bumpen:', error);
    }
  };

  // Alert dismissieren
  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  // Fullscreen Toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Aktive Alerts (nicht dismissed)
  const activeAlerts = data.alerts?.filter(a => !dismissedAlerts.has(a.alert_id)) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <ChefHat className="w-16 h-16 text-emerald-500 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-xl">KI-Küchenleiter wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-black/40 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo & Title */}
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl">
              <ChefHat className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">KI-Küchenleiter</h1>
              <p className="text-slate-400 text-sm">Intelligente Priorisierung • Live-Updates</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6">
            {/* Kitchen Load */}
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                data.stats?.kitchen_load === 'overloaded' ? 'text-red-400' :
                data.stats?.kitchen_load === 'busy' ? 'text-orange-400' : 'text-emerald-400'
              }`}>
                {data.stats?.total_orders || 0}
              </div>
              <div className="text-xs text-slate-400">Offen</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {data.stats?.critical_count || 0}
              </div>
              <div className="text-xs text-slate-400">Kritisch</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-slate-300">
                {data.stats?.avg_wait_time || 0} min
              </div>
              <div className="text-xs text-slate-400">Ø Wartezeit</div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-slate-400 hover:text-white"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-slate-400 hover:text-white"
            >
              <Maximize className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Alerts */}
      <AnimatePresence>
        {activeAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-6 py-3 space-y-2"
          >
            {activeAlerts.map(alert => (
              <Alert key={alert.alert_id} alert={alert} onDismiss={handleDismissAlert} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - 3 Columns */}
      <main className="flex-1 p-6 flex gap-6 min-h-0">
        {/* Jetzt zubereiten */}
        <KitchenColumn
          title="Jetzt zubereiten"
          icon={Flame}
          orders={data.now}
          color="bg-red-500"
          bgColor="bg-red-500/5"
          onBump={handleBump}
        />

        {/* Als Nächstes */}
        <KitchenColumn
          title="Als Nächstes"
          icon={Clock}
          orders={data.next}
          color="bg-orange-500"
          bgColor="bg-orange-500/5"
          onBump={handleBump}
        />

        {/* Kann warten */}
        <KitchenColumn
          title="Kann warten"
          icon={Timer}
          orders={data.later}
          color="bg-slate-500"
          bgColor="bg-slate-500/5"
          onBump={handleBump}
        />
      </main>

      {/* Footer */}
      <footer className="bg-black/40 border-t border-white/10 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>
            Letzte Aktualisierung: {new Date().toLocaleTimeString('de-DE')}
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Dringend
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Hoch
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Normal
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
              Niedrig
            </span>
          </div>
          <div>
            Tippen auf Bestellung = Nächster Status
          </div>
        </div>
      </footer>
    </div>
  );
}
